import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, BarChart3, Workflow, Users, ClipboardList, Package, Receipt,
  Briefcase, Mail, LifeBuoy, Inbox, AlertTriangle, ChevronsRight, RefreshCw,
  PackageOpen, ChevronRight, ListChecks, Printer, FileText, MessageSquare,
} from 'lucide-react';
import { useAppShell } from '../../contexts/AppShellContext';
import { useAuth } from '../../contexts/AuthContext';
import { pageVisible, can } from '../../utils/rbac';
import { getMaterialReceipts } from '../../services/materialReceiptService';
import { resetDemoSeed } from '../../services/adminService';
import { useProxyData } from '../../contexts/ProxyDataContext';
import { useEmailInbox } from '../../contexts/EmailInboxContext';
import { buildProxyIntakeTasks, isProxyTaskException, escalationFor } from '../../data/proxyWorkflowRegistry';
import { buildFollowUpTracker, isTrackerRowDue } from '../../utils/proxyFollowUpTracker';
import { PROXY_SEED_TODAY } from '../../data/mockProxyJobs';
import type { MaterialReceiptRow } from '../../types/api';

const ICON = { size: 16, strokeWidth: 1.75 };
const ICON_SM = { size: 14, strokeWidth: 1.75 };
const PILL_TRANSITION = { type: 'spring', stiffness: 500, damping: 40 };

const NAV_ITEMS = [
  {
    id: 'ace',
    path: '/ace',
    label: 'Ace',
    icon: <Sparkles {...ICON} />,
  },
];

const NAV_ITEMS_DATA = [
  {
    id: 'extraction-accuracy',
    path: '/analytics/accuracy-dashboard',
    label: 'Dashboard',
    icon: <BarChart3 {...ICON} />,
  },
];

const NAV_ITEMS_ADMIN = [
  {
    id: 'workflows',
    path: '/workflows',
    label: 'Workflows',
    icon: <Workflow {...ICON} />,
  },
  {
    id: 'admin-users',
    path: '/admin/users',
    label: 'Admin Dashboard',
    icon: <Users {...ICON} />,
  },
  {
    id: 'admin-feedback',
    path: '/admin/feedback',
    label: 'Feedback',
    icon: <MessageSquare {...ICON} />,
  },
];

// Temporarily hidden from the prototype nav — remove this set to restore.
const DEMO_HIDDEN = new Set(['inventory', 'email-intake', 'advisor-support', 'workflows', 'overs', 'fulfillment-email-inbox']);

const NAV_ITEMS_FULFILLMENT = [
  {
    id: 'receiving-log',
    path: '/receiving-log',
    label: 'Receiving Log',
    icon: <ClipboardList {...ICON} />,
  },
  {
    // A record, not an intake state — sibling of Receiving Log rather than a
    // Material Intake queue child.
    id: 'production-log',
    path: '/production-log',
    label: 'Production Log',
    icon: <Printer {...ICON} />,
  },
  {
    id: 'inventory',
    path: '/inventory',
    label: 'Inventory',
    icon: <Package {...ICON} />,
  },
  {
    id: 'all-receipts',
    path: '/all-receipts',
    label: 'All Receipts',
    icon: <Receipt {...ICON} />,
  },
  {
    id: 'jobs',
    path: '/jobs',
    label: 'All Jobs',
    icon: <Briefcase {...ICON} />,
  },
  {
    // Same shared Email Inbox as Proxy's own nav entry below, defaulting to
    // the Fulfillment tab — this is where Wayne sees Ace's sent shortage
    // outreach and the solicitor's reply in one thread.
    id: 'fulfillment-email-inbox',
    path: '/email-inbox/fulfillment',
    label: 'Email Inbox',
    icon: <Mail {...ICON} />,
  },
];

const NAV_ITEMS_CORPORATE = [
  {
    id: 'email-intake',
    path: '/email-intake',
    label: 'Email Intake',
    icon: <Mail {...ICON} />,
  },
  {
    id: 'advisor-support',
    path: '/advisor-support',
    label: 'Advisor Support',
    icon: <LifeBuoy {...ICON} />,
  },
];

// Shared by both material-type tabs — Exceptions/Escalations aren't
// Proxy-specific.
const INTAKE_CHILDREN_COMMON = [
  {
    id: 'exceptions',
    path: '/exceptions',
    label: 'Exceptions',
    badgeColor: 'bg-[#e7000b]',
    icon: <AlertTriangle {...ICON_SM} />,
  },
  {
    id: 'escalations',
    path: '/escalations',
    label: 'Escalations',
    badgeColor: 'bg-[#6d28d9]',
    icon: <ChevronsRight {...ICON_SM} />,
  },
];

// Proxy/Corp Action-only — Regulatory has no Follow Ups/Overs concept of its
// own (its equivalent states live inside the Regulatory work queue itself).
const INTAKE_CHILDREN_PROXY_ONLY = [
  {
    id: 'follow-ups',
    path: '/follow-ups',
    label: 'Follow Ups',
    badgeColor: 'bg-[#6d28d9]',
    icon: <RefreshCw {...ICON_SM} />,
  },
  {
    id: 'overs',
    path: '/overs',
    label: 'Overs',
    badgeColor: 'bg-[#f59e0b]',
    icon: <PackageOpen {...ICON_SM} />,
  },
];

const INTAKE_QUEUE_ITEM = {
  id: 'intake-queue',
  path: '/intake-queue',
  label: 'Material Intake',
  icon: <Inbox {...ICON} />,
};

// PROXY (ICT/Collections) — a separate user group from Material Intake's
// dock/receiving team. Deliberately unconditional / not run through
// pageVisible() — this section has zero permission gating by design.
// Its own Intake Queue (see data/proxyWorkflowRegistry.ts) — badge is the
// count of open tasks across every registered Proxy ACE workflow.
// Nested under Proxy's Intake Queue, mirroring Material Intake's own bucket
// shape — deliberately no "Overs" child, that bucket is physical-receiving
// only and has no Proxy equivalent.
const PROXY_INTAKE_CHILDREN = [
  {
    id: 'proxy-exceptions',
    path: '/proxy/exceptions',
    label: 'Exceptions',
    badgeColor: 'bg-[#e7000b]',
    icon: <AlertTriangle {...ICON_SM} />,
  },
  {
    id: 'proxy-escalations',
    path: '/proxy/escalations',
    label: 'Escalations',
    badgeColor: 'bg-[#6d28d9]',
    icon: <ChevronsRight {...ICON_SM} />,
  },
  {
    id: 'proxy-follow-ups',
    path: '/proxy/follow-ups',
    label: 'Follow Ups',
    badgeColor: 'bg-[#6d28d9]',
    icon: <RefreshCw {...ICON_SM} />,
  },
];

// Passive/read-only stores — mirror Fulfillment's All Receipts + Receiving
// Log shape, flat items alongside the expandable Intake Queue group.
const NAV_ITEMS_PROXY_STORES = [
  {
    id: 'proxy-emails',
    path: '/email-inbox',
    label: 'Email Inbox',
    icon: <Mail {...ICON} />,
  },
  // Where jobs stand: All outstanding, Red, Yellow, Green, the Red Report
  // and the manual pull.
  {
    id: 'proxy-reports',
    path: '/proxy/reports',
    label: 'Reports',
    icon: <FileText {...ICON} />,
  },
  {
    id: 'proxy-completed',
    path: '/proxy/completed',
    label: 'Completed',
    icon: <ClipboardList {...ICON} />,
  },
];

const PROXY_INTAKE_ITEM = {
  id: 'proxy-intake',
  path: '/proxy',
  label: 'Intake Queue',
  // Distinct from Material Intake's Inbox icon (collapsed rail shows icons
  // only — same icon for two different sections' "Intake Queue" would be
  // ambiguous at a glance).
  icon: <ListChecks {...ICON} />,
  badgeColor: 'bg-[#e7000b]',
};

function SectionLabel({ label, isCollapsed }) {
  if (isCollapsed) return null;
  return (
    <div className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-[#A0A3AB]">
      {label}
    </div>
  );
}

// Delayed hover open/close so moving the mouse from the rail icon to the
// flyout panel (across the gap between them) doesn't flicker it shut.
function useDelayedHover(openDelay = 120, closeDelay = 200) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => { if (timer.current) clearTimeout(timer.current); };
  const onEnter = () => { clear(); timer.current = setTimeout(() => setOpen(true), openDelay); };
  const onLeave = () => { clear(); timer.current = setTimeout(() => setOpen(false), closeDelay); };
  const close   = () => { clear(); setOpen(false); };

  useEffect(() => clear, []);

  return { open, onEnter, onLeave, close };
}

// Portals a floating panel next to a rail icon — needed because the <aside>
// has overflow-hidden, which would clip an absolutely-positioned child.
function RailFlyout({ anchorRef, open, onMouseEnter, onMouseLeave, children }) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (open && anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
  }, [open, anchorRef]);

  if (!open || !rect) return null;

  return createPortal(
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="fixed z-[200] bg-white border border-trim rounded-xl shadow-panel py-1.5 min-w-[180px]"
      style={{ top: rect.top, left: rect.right + 8 }}
    >
      {children}
    </div>,
    document.body
  );
}

function NavItem({ item, isCollapsed }) {
  const anchorRef = useRef(null);
  const { open, onEnter, onLeave, close } = useDelayedHover();

  if (!isCollapsed) {
    return <NavItemLink item={item} isCollapsed={false} />;
  }

  return (
    <>
      <NavItemLink
        item={item}
        isCollapsed
        anchorRef={anchorRef}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onClick={close}
      />
      <RailFlyout anchorRef={anchorRef} open={open} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <div className="px-3 py-1.5 text-sm font-medium text-ink whitespace-nowrap">{item.label}</div>
      </RailFlyout>
    </>
  );
}

function NavItemLink({ item, isCollapsed, anchorRef, onMouseEnter, onMouseLeave, onClick }: any) {
  return (
    <NavLink
      ref={anchorRef}
      to={item.path}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-2.5 py-2 mx-2 rounded-lg text-sm font-normal cursor-pointer transition-colors duration-100 relative whitespace-nowrap overflow-hidden
         ${isCollapsed ? 'justify-center mx-1.5 px-2' : 'px-3.5'}
         ${isActive
           ? 'text-navy font-medium'
           : 'text-copy hover:bg-tint hover:text-navy'
         }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId="sidebar-active-pill"
              className="absolute inset-0 rounded-lg bg-tint"
              transition={PILL_TRANSITION}
            />
          )}
          <span className={`relative z-10 w-4 h-4 flex-shrink-0 flex items-center justify-center transition-opacity ${isActive ? 'opacity-100' : 'opacity-70'}`}>
            {item.icon}
          </span>
          {!isCollapsed && (
            <>
              <span className="relative z-10 nav-text text-sm">{item.label}</span>
              {item.badge > 0 && (
                <span className={`relative z-10 ml-auto text-xs font-semibold text-white px-1.5 py-px rounded-full flex-shrink-0 leading-snug ${item.badgeColor}`}>
                  {item.badge}
                </span>
              )}
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

function ExpandableNavGroup({ item, isCollapsed, counts = {} }) {
  const location = useLocation();
  const childActive = (c) => location.pathname === c.path;
  const isAnyChildActive = item.children.some(childActive);
  const isOwnActive      = location.pathname === item.path;
  const isParentActive   = isOwnActive || isAnyChildActive;
  const [expanded, setExpanded] = useState(true);
  const anchorRef = useRef(null);
  const { open, onEnter, onLeave, close } = useDelayedHover();

  useEffect(() => {
    if (isAnyChildActive) setExpanded(true);
  }, [isAnyChildActive]);

  const aggregateBadge = item.children.reduce((sum, c) => sum + (counts[c.id] || 0), 0);

  if (isCollapsed) {
    return (
      <>
        <NavLink
          ref={anchorRef}
          to={item.path}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          onClick={close}
          className={`relative flex items-center justify-center py-2 mx-1.5 rounded-lg cursor-pointer transition-colors duration-100
             ${isParentActive
               ? 'text-navy'
               : 'text-copy hover:bg-tint hover:text-navy'
             }`}
        >
          {isParentActive && (
            <motion.span
              layoutId="sidebar-active-pill"
              className="absolute inset-0 rounded-lg bg-tint"
              transition={PILL_TRANSITION}
            />
          )}
          <span className="relative z-10 w-4 h-4 flex items-center justify-center opacity-80">
            {item.icon}
          </span>
          {aggregateBadge > 0 && (
            <span className="absolute z-10 -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#e7000b] text-white text-xs font-bold flex items-center justify-center leading-none">
              {aggregateBadge}
            </span>
          )}
        </NavLink>
        <RailFlyout anchorRef={anchorRef} open={open} onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="px-3 pt-1 pb-1.5 text-xs font-semibold uppercase tracking-wider text-[#A0A3AB] whitespace-nowrap">
            {item.label}
          </div>
          {item.children.map(child => {
            const badge = counts[child.id] || 0;
            return (
              <NavLink
                key={child.id}
                to={child.path}
                onClick={close}
                className="flex items-center gap-2 px-3 py-2 text-sm text-copy hover:bg-tint hover:text-navy whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center opacity-70">
                  {child.icon}
                </span>
                <span>{child.label}</span>
                {badge > 0 && (
                  <span className={`ml-auto text-xs font-semibold text-white px-1.5 py-px rounded-full flex-shrink-0 leading-snug ${child.badgeColor}`}>
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </RailFlyout>
      </>
    );
  }

  return (
    <div>
      {/* Parent row */}
      <div className="flex items-center mx-2 rounded-lg overflow-hidden">
        <NavLink
          to={item.path}
          className="flex-1 min-w-0 flex items-center gap-2 pl-3 pr-1 py-2 rounded-lg text-sm font-normal cursor-pointer transition-colors duration-100 relative overflow-hidden"
        >
          {isOwnActive && (
            <motion.span
              layoutId="sidebar-active-pill"
              className="absolute inset-0 rounded-lg bg-tint"
              transition={PILL_TRANSITION}
            />
          )}
          {!isOwnActive && isAnyChildActive && (
            <span className="absolute inset-0 rounded-lg bg-tint" />
          )}
          <span className={`relative z-10 w-4 h-4 flex-shrink-0 flex items-center justify-center ${isParentActive ? 'opacity-100 text-navy' : 'opacity-70 text-copy'}`}>
            {item.icon}
          </span>
          <span className={`relative z-10 text-sm truncate ${isParentActive ? 'text-navy font-medium' : 'text-copy'}`}>{item.label}</span>
        </NavLink>
        {/* Aggregate badge — kept OUTSIDE the NavLink's overflow-hidden box so a
            long label (or a 2-digit count) never clips it against the chevron. */}
        {!expanded && aggregateBadge > 0 && (
          <span className="text-[10px] font-semibold text-white px-1 py-px rounded-full flex-shrink-0 leading-tight bg-[#e7000b] ml-1">
            {aggregateBadge}
          </span>
        )}
        {/* Chevron toggle — separate from NavLink */}
        <button
          onClick={() => setExpanded(v => !v)}
          className="relative z-10 p-1 mr-0.5 rounded-md flex-shrink-0 text-muted hover:bg-tint hover:text-navy transition-colors"
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <motion.span
            className="flex items-center justify-center"
            animate={{ rotate: expanded ? 90 : 0 }}
            transition={{ duration: 0.15 }}
          >
            <ChevronRight size={12} strokeWidth={1.75} />
          </motion.span>
        </button>
      </div>

      {/* Sub-items — active state computed from ?status= (NavLink's default
          pathname match would light up every child at once) */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="intake-children"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="ml-5 pl-3 border-l border-trim mt-0.5 mb-0.5 mr-2">
              {item.children.map(child => {
                const active = childActive(child);
                const badge = counts[child.id] || 0;
                return (
                  <NavLink
                    key={child.id}
                    to={child.path}
                    className="flex items-center gap-2 px-2.5 py-2.5 min-h-[44px] rounded-lg text-sm font-normal cursor-pointer transition-colors duration-100 relative whitespace-nowrap overflow-hidden"
                  >
                    {active && (
                      <motion.span
                        layoutId="sidebar-active-pill"
                        className="absolute inset-0 rounded-lg bg-tint"
                        transition={PILL_TRANSITION}
                      />
                    )}
                    <span className={`relative z-10 w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center ${active ? 'opacity-100 text-navy' : 'opacity-60 text-copy'}`}>
                      {child.icon}
                    </span>
                    <span className={`relative z-10 text-xs ${active ? 'text-navy font-medium' : 'text-copy'}`}>{child.label}</span>
                    {badge > 0 && (
                      <span className={`relative z-10 ml-auto text-xs font-semibold text-white px-1.5 py-px rounded-full flex-shrink-0 leading-snug ${child.badgeColor}`}>
                        {badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


export function Sidebar() {
  const { isCollapsed, materialIntakeTab } = useAppShell();
  const { user } = useAuth();
  const { jobs: proxyJobs } = useProxyData();
  const { proxyEmails } = useEmailInbox();
  const [resetting, setResetting] = useState(false);

  // RBAC page visibility (utils/rbac.ts) — purely presentational; the backend
  // enforces every verb behind these pages. Team roles (Wayne, Cary) see
  // every page — no restriction.
  const visible = (id: string) => pageVisible(user, id);
  const navItems        = NAV_ITEMS.filter(i => visible(i.id) && !DEMO_HIDDEN.has(i.id));
  const fulfillItems    = NAV_ITEMS_FULFILLMENT.filter(i => visible(i.id) && !DEMO_HIDDEN.has(i.id));
  const corporateItems  = NAV_ITEMS_CORPORATE.filter(i => visible(i.id) && !DEMO_HIDDEN.has(i.id));
  const dataItems       = NAV_ITEMS_DATA.filter(i => visible(i.id));
  const adminItems      = NAV_ITEMS_ADMIN.filter(i => visible(i.id) && !DEMO_HIDDEN.has(i.id));
  // Follow Ups/Overs are Proxy/Corp Action-only — only show them alongside
  // Exceptions/Escalations while that material-type tab is the active one.
  const intakeChildrenForTab = materialIntakeTab === 'regulatory'
    ? INTAKE_CHILDREN_COMMON
    : [...INTAKE_CHILDREN_COMMON, ...INTAKE_CHILDREN_PROXY_ONLY];
  const intakeChildren  = intakeChildrenForTab.filter(c => visible(c.id) && !DEMO_HIDDEN.has(c.id));
  const showIntakeGroup = visible(INTAKE_QUEUE_ITEM.id);
  const intakeItem      = { ...INTAKE_QUEUE_ITEM, children: intakeChildren };

  // Live counts for the Material Intake sub-items. Lighter poll than the
  // pages (15s vs 3s) — pills tolerate a little staleness. The same fetch
  // also feeds the PROXY badges' live cross-reference below (incl. the
  // SHORTAGE_FOLLOWUP workflow's Fulfillment-escalation trigger), at zero
  // extra network cost.
  const [intakeCounts, setIntakeCounts] = useState<Record<string, number>>({});
  const [liveReceipts, setLiveReceipts] = useState<MaterialReceiptRow[] | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => getMaterialReceipts().then(rows => {
      if (!alive) return;
      setLiveReceipts(rows);
      setIntakeCounts({
        exceptions: rows.filter(r => !r.escalated_to_team && ['No Job Found', 'Received Short', 'On Hold'].includes(r.material_status)).length,
        escalations: rows.filter(r => !!r.escalated_to_team).length,
        'follow-ups': rows.filter(r => r.material_status === 'Follow Ups').length,
        overs: rows.filter(r => r.overs_status === 'Pending').length,
      });
    }).catch(() => { /* unauthenticated / transient — pills just stay hidden */ });
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // PROXY Intake Queue badges — parent = count of open tasks across every
  // registered ACE workflow; children = the same bucket predicates the
  // Exceptions/Escalations/Follow-Ups pages use.
  const proxyTaskRows = React.useMemo(() => {
    const tasks = buildProxyIntakeTasks({ jobs: proxyJobs, emails: proxyEmails, receipts: liveReceipts });
    return tasks.map(task => ({ task, job: proxyJobs.find(j => j.jobId === task.jobId)! })).filter(r => r.job);
  }, [proxyJobs, proxyEmails, liveReceipts]);
  const proxyBucketCounts = React.useMemo(() => ({
    'proxy-exceptions': proxyTaskRows.filter(r => isProxyTaskException(r.task, r.job)).length,
    'proxy-escalations': proxyTaskRows.filter(r => escalationFor(r.task, r.job, proxyEmails)).length,
    // Follow-ups due or overdue today — the Follow Ups tracker's own count.
    'proxy-follow-ups': buildFollowUpTracker(proxyJobs, liveReceipts, PROXY_SEED_TODAY, proxyEmails).filter(isTrackerRowDue).length,
  }), [proxyTaskRows, proxyJobs, liveReceipts, proxyEmails]);

  return (
    <aside
      className="flex flex-col flex-shrink-0 bg-white border-r border-trim overflow-hidden transition-all duration-200"
      style={{ width: isCollapsed ? 52 : 216 }}
    >
      {/* Scrollable nav — everything above Admin can outgrow the viewport
          (many optional sections stack up), so this region scrolls on its
          own while Admin + Reset stay pinned below it. min-h-0 overrides the
          flex item's default min-height:auto, which would otherwise stop
          this from shrinking and defeat overflow-y-auto. */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Nav group 1 — Ace + Receiving Log */}
        {navItems.length > 0 && (
          <div className="py-2 flex-shrink-0">
            {navItems.map(item => <NavItem key={item.id} item={item} isCollapsed={isCollapsed} />)}
          </div>
        )}

        <div className="h-px bg-trim mx-3.5" />

        {/* PROXY (ICT/Collections) — deliberately unconditional, no visible()/
            pageVisible() filtering. Zero permission gating by design; every
            "escalate to manager" case is an informational chip, never a
            disabled control. */}
        <div className="py-1.5 flex-shrink-0">
          <SectionLabel label="PROXY" isCollapsed={isCollapsed} />
          <ExpandableNavGroup
            item={{ ...PROXY_INTAKE_ITEM, children: PROXY_INTAKE_CHILDREN }}
            isCollapsed={isCollapsed}
            counts={proxyBucketCounts}
          />
          {NAV_ITEMS_PROXY_STORES.map(item => <NavItem key={item.id} item={item} isCollapsed={isCollapsed} />)}
        </div>

        {(showIntakeGroup || intakeChildren.length > 0 || fulfillItems.length > 0) && (
          <>
            <div className="h-px bg-trim mx-3.5" />

            {/* Nav group 2 — Fulfillment */}
            <div className="py-1.5 flex-shrink-0">
              <SectionLabel label="Fulfillment" isCollapsed={isCollapsed} />
              {showIntakeGroup && (
                <ExpandableNavGroup item={intakeItem} isCollapsed={isCollapsed} counts={intakeCounts} />
              )}
              {/* Visible inbox children whose parent page isn't (e.g. Cary's
                  Escalations) render as plain items. */}
              {!showIntakeGroup && intakeChildren.map(child => (
                <NavItem
                  key={child.id}
                  item={{ ...child, badge: intakeCounts[child.id] || 0 }}
                  isCollapsed={isCollapsed}
                />
              ))}
              {fulfillItems.map(item => <NavItem key={item.id} item={item} isCollapsed={isCollapsed} />)}
            </div>
          </>
        )}

        {corporateItems.length > 0 && (
          <>
            <div className="h-px bg-trim mx-3.5" />

            {/* Nav group 3 — Corporate Actions */}
            <div className="py-1.5 flex-shrink-0">
              <SectionLabel label="Corporate Actions" isCollapsed={isCollapsed} />
              {corporateItems.map(item => <NavItem key={item.id} item={item} isCollapsed={isCollapsed} />)}
            </div>
          </>
        )}

        {dataItems.length > 0 && (
          <>
            <div className="h-px bg-trim mx-3.5" />
            <div className="py-1.5 flex-shrink-0">
              <SectionLabel label="Governance" isCollapsed={isCollapsed} />
              {dataItems.map(item => <NavItem key={item.id} item={item} isCollapsed={isCollapsed} />)}
            </div>
          </>
        )}
      </div>

      {/* Admin group — pinned to bottom */}
      {adminItems.length > 0 && (
        <>
          <div className="h-px bg-trim mx-3.5" />
          <div className="py-1.5 flex-shrink-0">
            <SectionLabel label="Admin" isCollapsed={isCollapsed} />
            {adminItems.map(item => <NavItem key={item.id} item={item} isCollapsed={isCollapsed} />)}
          </div>
        </>
      )}

      {!isCollapsed && can(user, 'user.manage') && (
        <>
          <div className="h-px bg-trim mx-3.5" />
          <div className="py-2 px-2 flex-shrink-0">
            <button
              type="button"
              onClick={async () => {
                setResetting(true);
                try {
                  const r = await resetDemoSeed();
                  // Ace chat threads and the shared Email Inbox are frontend-
                  // only (localStorage) — resetting the backend seed alone
                  // leaves stale sessions/messages behind for jobs the demo
                  // just re-seeded, so clear them here too.
                  window.localStorage.removeItem('opsai.chats.v1');
                  window.localStorage.removeItem('opsai.emailinbox.v1');
                  window.localStorage.removeItem('opsai.emailinbox.deleted.v1');
                  window.localStorage.removeItem('opsai.emailinbox.categories.v1');
                  window.localStorage.removeItem('opsai.proxyapprovals.v1');
                  // Proxy's jobs are frontend-only too: drop the saved changes
                  // so Proxy goes back to its starting demo set.
                  window.localStorage.removeItem('opsai.proxyjobs.v1');
                  window.localStorage.removeItem('opsai.teammessages.v1');
                  window.localStorage.removeItem('opsai.teammessages.read.v1');
                  window.alert(`Reset ${r.scans} canonical scans (${r.created} created, ${r.updated} updated). Refresh to see the reseeded state.`);
                } catch {
                  window.alert('Reset failed — check the backend logs.');
                } finally {
                  setResetting(false);
                }
              }}
              disabled={resetting}
              className="w-full px-1.5 py-1.5 text-xs font-medium rounded-lg text-copy border border-trim hover:bg-tint transition-colors disabled:opacity-50"
            >
              {resetting ? 'Resetting…' : 'Reset demo data'}
            </button>
          </div>
        </>
      )}
    </aside>
  );
}
